import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { QRCodeSVG } from "qrcode.react";

const VBAR_CONFIG_VERSION = "VBAR_CONFIG_V1.1.0";
const VBAR_CONFIG_PREFIX = `___${VBAR_CONFIG_VERSION}___`;
const VBAR_SIGNATURE_SECRET = "1234567887654321";

const TURNSTILE_QR_BASE_CONFIG = {
  w_mode: 1,
  ochannel: 64,
  nochannel: 64,
  owifi: 2,
  de_type: 1,
  devnum: 1002,
  houttime: 5,
  ip_mode: 1,
};

const normalizeDevnum = (value) => {
  const raw = String(value ?? "").trim();
  const parsed = Number(raw);
  return raw !== "" && Number.isFinite(parsed) ? parsed : raw;
};

const toUtf8Bytes = (value) => Array.from(new TextEncoder().encode(value));

const bytesToBase64 = (bytes) => {
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return window.btoa(binary);
};

const rotateLeft = (value, shift) =>
  (value << shift) | (value >>> (32 - shift));

const MD5_SHIFTS = [
  7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 5, 9, 14, 20, 5,
  9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11,
  16, 23, 4, 11, 16, 23, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10,
  15, 21,
];

const MD5_K = Array.from({ length: 64 }, (_, i) =>
  Math.floor(Math.abs(Math.sin(i + 1)) * 2 ** 32),
);

const md5Bytes = (inputBytes) => {
  const bytes = [...inputBytes];
  const bitLength = bytes.length * 8;
  bytes.push(0x80);
  while (bytes.length % 64 !== 56) bytes.push(0);
  for (let i = 0; i < 8; i += 1) {
    bytes.push(Math.floor(bitLength / 2 ** (8 * i)) & 0xff);
  }

  let a0 = 0x67452301;
  let b0 = 0xefcdab89;
  let c0 = 0x98badcfe;
  let d0 = 0x10325476;

  for (let offset = 0; offset < bytes.length; offset += 64) {
    const words = Array.from({ length: 16 }, (_, i) => {
      const j = offset + i * 4;
      return (
        bytes[j] |
        (bytes[j + 1] << 8) |
        (bytes[j + 2] << 16) |
        (bytes[j + 3] << 24)
      );
    });

    let a = a0;
    let b = b0;
    let c = c0;
    let d = d0;

    for (let i = 0; i < 64; i += 1) {
      let f;
      let g;
      if (i < 16) {
        f = (b & c) | (~b & d);
        g = i;
      } else if (i < 32) {
        f = (d & b) | (~d & c);
        g = (5 * i + 1) % 16;
      } else if (i < 48) {
        f = b ^ c ^ d;
        g = (3 * i + 5) % 16;
      } else {
        f = c ^ (b | ~d);
        g = (7 * i) % 16;
      }

      const nextD = d;
      d = c;
      c = b;
      b =
        (b +
          rotateLeft((a + f + MD5_K[i] + words[g]) | 0, MD5_SHIFTS[i])) |
        0;
      a = nextD;
    }

    a0 = (a0 + a) | 0;
    b0 = (b0 + b) | 0;
    c0 = (c0 + c) | 0;
    d0 = (d0 + d) | 0;
  }

  return [a0, b0, c0, d0].flatMap((word) => [
    word & 0xff,
    (word >>> 8) & 0xff,
    (word >>> 16) & 0xff,
    (word >>> 24) & 0xff,
  ]);
};

const hmacMd5Base64 = (message, secret) => {
  const blockSize = 64;
  let key = toUtf8Bytes(secret);
  if (key.length > blockSize) key = md5Bytes(key);
  while (key.length < blockSize) key.push(0);

  const outerKey = key.map((byte) => byte ^ 0x5c);
  const innerKey = key.map((byte) => byte ^ 0x36);
  const innerHash = md5Bytes([...innerKey, ...toUtf8Bytes(message)]);
  return bytesToBase64(md5Bytes([...outerKey, ...innerHash]));
};

const generateVbarSignature = (payload) => {
  return hmacMd5Base64(payload, VBAR_SIGNATURE_SECRET);
};

const buildVbarConfigPayload = (config) => {
  return (
    `${VBAR_CONFIG_PREFIX}{` +
    `w_mode=${config.w_mode},` +
    `ochannel=${config.ochannel},` +
    `nochannel=${config.nochannel},` +
    `owifi=${config.owifi},` +
    `de_type=${config.de_type},` +
    `s_mode=${config.s_mode},` +
    `devnum=${config.devnum},` +
    `haddr="${config.haddr}",` +
    `houttime=${config.houttime},` +
    `interval=${config.interval},` +
    `door_report=${config.door_report},` +
    `ip_mode=${config.ip_mode},` +
    `ip="${config.ip}",` +
    `mask="${config.mask}",` +
    `gateway="${config.gateway}",` +
    `dns="${config.dns}"` +
    `}`
  );
};

export const buildTurnstileQrConfig = (turnstilesSettings, ip, qrCodeId) => {
  const scannerSMode =
    turnstilesSettings?.scannerSMode != null
      ? turnstilesSettings.scannerSMode
      : true;
  const scannerIntervalSec =
    turnstilesSettings?.scannerInterval != null
      ? parseInt(turnstilesSettings.scannerInterval, 10)
      : 5;
  const scannerInterval =
    Number.isFinite(scannerIntervalSec) && scannerIntervalSec >= 0
      ? scannerIntervalSec * 1000
      : 5000;
  const scannerDoorReport =
    turnstilesSettings?.scannerDoorReport != null
      ? turnstilesSettings.scannerDoorReport
      : false;

  const config = {
    ...TURNSTILE_QR_BASE_CONFIG,
    devnum: normalizeDevnum(qrCodeId),
    s_mode: scannerSMode ? 0 : 1,
    interval: scannerInterval,
    door_report: scannerDoorReport ? 1 : 0,
    haddr: `http://${String(
      turnstilesSettings?.dlaServerIp ?? "",
    ).trim()}:2002/api/v1/access-management/datavision/scan`,
    ip: String(ip ?? "").trim(),
    mask: String(turnstilesSettings?.mask ?? "").trim(),
    gateway: String(turnstilesSettings?.gateway ?? "").trim(),
    dns: String(turnstilesSettings?.dns ?? "").trim(),
  };

  const payload = buildVbarConfigPayload(config);
  const signature = generateVbarSignature(payload);

  return {
    configVersion: VBAR_CONFIG_VERSION,
    config,
    payload,
    signature,
    qrValue: `${payload}--${signature}`,
  };
};

const TurnstileQrBlock = ({ label, config, t }) => {
  const qrValue = config?.qrValue ?? "";

  const rawConfig = React.useMemo(() => {
    if (!config) return "";

    return [
      config.qrValue,
      "",
      "Parsed config:",
      JSON.stringify(
        {
          configVersion: config.configVersion,
          ...config.config,
          signature: config.signature,
        },
        null,
        2,
      ),
    ].join("\n");
  }, [config]);

  return (
    <Box sx={{ flex: 1, minWidth: 280 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
        {label}
      </Typography>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          py: 2,
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            p: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            bgcolor: "background.paper",
          }}
        >
          <QRCodeSVG value={qrValue} size={220} />
        </Box>
      </Box>

      <TextField
        fullWidth
        multiline
        minRows={8}
        value={rawConfig}
        label={t(
          "dsokCustomerDetail.workspaces.devices.turnstile.qrModal.rawJson",
          {
            defaultValue: "QR konfigurácia",
          },
        )}
        InputProps={{ readOnly: true }}
      />
    </Box>
  );
};

const TurnstileQrCodeModal = ({ open, onClose, qrConfig, qrLabel, t }) => {
  const printRef = React.useRef(null);

  const handlePrint = React.useCallback(() => {
    const printContents = printRef.current?.innerHTML;
    if (!printContents) return;

    const printWindow = window.open("", "_blank", "width=800,height=900");
    if (!printWindow) return;

    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>${t(
            "dsokCustomerDetail.workspaces.devices.turnstile.qrModal.title",
            {
              defaultValue: "QR konfigurácia čítačky",
            },
          )}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 24px; }
            svg { max-width: 100%; height: auto; }
            textarea { width: 100%; min-height: 220px; font-family: monospace; }
          </style>
        </head>
        <body>${printContents}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  }, [t]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t("dsokCustomerDetail.workspaces.devices.turnstile.qrModal.title", {
          defaultValue: "QR konfigurácia čítačky",
        })}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} ref={printRef}>
          {qrConfig ? (
            <TurnstileQrBlock label={qrLabel} config={qrConfig} t={t} />
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handlePrint} variant="contained" disabled={!qrConfig}>
          {t("buttons.print", { defaultValue: "Tlačiť" })}
        </Button>
        <Button onClick={onClose}>
          {t("buttons.close", { defaultValue: "Zavrieť" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TurnstileQrCodeModal;
